import React, { useState, useEffect } from 'react';
import { ManagedUser, AccessLevel, ScopeTree } from '../../types';
import { settingsService } from '../../services/api';
import { formatDateTime } from '../../utils/datetime';
import { loadScopeTree } from '../ScopeFilter';

// How much of the hierarchy an account reaches. Widest first, because that is
// the order the consequences run in and the order the table reads best.
const ACCESS_LEVELS: { value: AccessLevel; label: string; hint: string }[] = [
  { value: 'super', label: 'Super User', hint: 'Every territory and domain' },
  { value: 'territory', label: 'Territory', hint: 'One territory and all its domains' },
  { value: 'domain', label: 'Domain', hint: 'A single domain' },
];

// The territory/domain pair an account is bound to. Which pulldowns apply
// depends on the level, so the control renders from it rather than the caller
// having to remember that a super user's scope fields are meaningless.
const LevelScope: React.FC<{
  level: AccessLevel;
  territory: string;
  domain: string;
  onChange: (level: AccessLevel, territory: string, domain: string) => void;
}> = ({ level, territory, domain, onChange }) => {
  const [tree, setTree] = useState<ScopeTree | null>(null);
  useEffect(() => { loadScopeTree().then(setTree).catch(() => {}); }, []);

  const domains = tree?.territories.find(t => t.name === territory)?.domains ?? [];

  return (
    <>
      <select
        value={level}
        title={ACCESS_LEVELS.find(l => l.value === level)?.hint}
        // Dropping to a narrower level clears the scope below it, so a
        // half-filled pair can never be submitted.
        onChange={e => onChange(e.target.value as AccessLevel, territory, domain)}
      >
        {ACCESS_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
      </select>
      {level !== 'super' && (
        <select
          value={territory}
          onChange={e => onChange(level, e.target.value, '')}
        >
          <option value="">— territory —</option>
          {(tree?.territories ?? []).map(t => (
            <option key={t.name} value={t.name}>{t.display_name}</option>
          ))}
        </select>
      )}
      {level === 'domain' && (
        <select
          value={domain}
          disabled={!territory}
          onChange={e => onChange(level, territory, e.target.value)}
        >
          <option value="">— domain —</option>
          {domains.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      )}
    </>
  );
};

const UsersPanel: React.FC = () => {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newAllowedIps, setNewAllowedIps] = useState('');
  // New accounts start at the narrowest level: an account created without a
  // deliberate choice should not be able to see the whole platform.
  const [newLevel, setNewLevel] = useState<AccessLevel>('domain');
  const [newTerritory, setNewTerritory] = useState('');
  const [newDomain, setNewDomain] = useState('');

  const [newCanAsk, setNewCanAsk] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editAllowedIps, setEditAllowedIps] = useState('');
  const [editLevel, setEditLevel] = useState<AccessLevel>('domain');
  const [editTerritory, setEditTerritory] = useState('');
  const [editDomain, setEditDomain] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await settingsService.listUsers());
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const parseIpList = (value: string): string[] =>
    value.split(',').map(s => s.trim()).filter(Boolean);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await settingsService.createUser({
        email: newEmail.trim(),
        name: newName.trim(),
        password: newPassword,
        allowed_login_ips: parseIpList(newAllowedIps),
        access_level: newLevel,
        territory: newTerritory,
        domain: newDomain,
        can_ask: newCanAsk,
      });
      setNewEmail('');
      setNewName('');
      setNewPassword('');
      setNewAllowedIps('');
      setNewLevel('domain');
      setNewTerritory('');
      setNewDomain('');
      setNewCanAsk(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const startEditing = (user: ManagedUser) => {
    setEditingId(user.id);
    setEditName(user.display_name);
    setEditPassword('');
    setEditAllowedIps(user.allowed_login_ips.join(', '));
    setEditLevel(user.access_level);
    setEditTerritory(user.territory);
    setEditDomain(user.domain);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
    setEditPassword('');
    setEditAllowedIps('');
  };

  const saveEditing = async (id: number) => {
    setError(null);
    try {
      await settingsService.updateUser({
        id,
        name: editName.trim(),
        password: editPassword || undefined,
        allowed_login_ips: parseIpList(editAllowedIps),
        access_level: editLevel,
        territory: editTerritory,
        domain: editDomain,
      });
      cancelEditing();
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  // Granted and revoked in one click, like Disable. A permission that is
  // meant to be given deliberately still has to be given easily, or it gets
  // given in a SQL client instead — where nobody else can see it happen.
  const toggleCanAsk = async (user: ManagedUser) => {
    setError(null);
    try {
      await settingsService.updateUser({ id: user.id, can_ask: !user.can_ask });
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const toggleActive = async (user: ManagedUser) => {
    setError(null);
    try {
      await settingsService.updateUser({ id: user.id, is_active: !user.is_active });
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const deleteUser = async (user: ManagedUser) => {
    setError(null);
    if (!window.confirm(`Permanently delete ${user.email}? This cannot be undone.`)) {
      return;
    }
    try {
      await settingsService.deleteUser(user.id);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  if (loading) return <div className="loading">Loading users...</div>;

  return (
    <div className="settings-panel">
      <h3>Users</h3>
      {error && <div className="error">{error}</div>}

      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th>User Account</th>
              <th>Name</th>
              <th>Sees</th>
              <th>Allowed login IPs</th>
              <th>Active</th>
              <th title="May use the Ask tab, which spends money on each question">Ask</th>
              <th>Last login</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td className="monospace">{user.email}</td>
                <td>
                  {editingId === user.id ? (
                    <input
                      type="text"
                      name={`display-name-${user.id}`}
                      autoComplete="off"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      placeholder="Name"
                    />
                  ) : (
                    user.display_name
                  )}
                </td>
                <td className="small">
                  {editingId === user.id ? (
                    <LevelScope
                      level={editLevel}
                      territory={editTerritory}
                      domain={editDomain}
                      onChange={(l, t, d) => { setEditLevel(l); setEditTerritory(t); setEditDomain(d); }}
                    />
                  ) : user.access_level === 'super' ? (
                    <span className="status status-active">everything</span>
                  ) : (
                    <span className="monospace">
                      {user.territory || '—'}
                      {user.access_level === 'domain' ? ` / ${user.domain || '—'}` : ' / all domains'}
                    </span>
                  )}
                </td>
                <td className="monospace small">
                  {editingId === user.id ? (
                    <input
                      type="text"
                      name={`allowed-ips-${user.id}`}
                      autoComplete="off"
                      value={editAllowedIps}
                      onChange={e => setEditAllowedIps(e.target.value)}
                      placeholder="empty = allow from anywhere"
                    />
                  ) : (
                    user.allowed_login_ips.length > 0 ? user.allowed_login_ips.join(', ') : 'anywhere'
                  )}
                </td>
                <td>
                  <span className={`status ${user.is_active ? 'status-completed' : 'status-failed'}`}>
                    {user.is_active ? 'active' : 'disabled'}
                  </span>
                </td>
                <td>
                  {user.can_ask
                    ? <span className="status status-completed">allowed</span>
                    : <span style={{ opacity: 0.35 }}>—</span>}
                </td>
                <td>{user.last_login_at ? formatDateTime(user.last_login_at) : 'never'}</td>
                <td>
                  {editingId === user.id ? (
                    <>
                      {/* autoComplete="new-password" stops the browser from
                          treating this row as a login form — without it,
                          Chrome/Safari inject the saved credentials, putting
                          the current login's email into the nearest text
                          input (the Allowed IPs field). */}
                      <input
                        type="password"
                        name={`new-password-${user.id}`}
                        autoComplete="new-password"
                        placeholder="new password (optional)"
                        value={editPassword}
                        onChange={e => setEditPassword(e.target.value)}
                        style={{ marginBottom: '6px', display: 'block' }}
                      />
                      <button type="button" onClick={() => saveEditing(user.id)}>Save</button>
                      <button type="button" onClick={cancelEditing}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={() => startEditing(user)}>Edit</button>
                      <button type="button" onClick={() => toggleActive(user)}>
                        {user.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleCanAsk(user)}
                        title={user.can_ask
                          ? 'Stop this account using the Ask tab'
                          : 'Let this account ask questions. Each one spends money.'}
                      >
                        {user.can_ask ? 'Revoke Ask' : 'Allow Ask'}
                      </button>
                      {!user.is_active && (
                        <button type="button" onClick={() => deleteUser(user)}>Delete</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>Add user</h3>
      {/* Field order mirrors the edit row (password last). autoComplete
          attributes stop the browser from filling the account field with
          the currently logged-in user's saved credentials. */}
      <form onSubmit={handleCreate} className="search-form settings-form" autoComplete="off">
        <input
          type="email"
          name="new-user-account"
          autoComplete="off"
          placeholder="User account (email)"
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          required
        />
        <input
          type="text"
          name="new-user-name"
          autoComplete="off"
          placeholder="Name"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          required
        />
        <LevelScope
          level={newLevel}
          territory={newTerritory}
          domain={newDomain}
          onChange={(l, t, d) => { setNewLevel(l); setNewTerritory(t); setNewDomain(d); }}
        />
        <input
          type="text"
          name="new-user-allowed-ips"
          autoComplete="off"
          placeholder="Allowed login IPs (comma-separated, empty = anywhere)"
          value={newAllowedIps}
          onChange={e => setNewAllowedIps(e.target.value)}
        />
        <input
          type="password"
          name="new-user-password"
          autoComplete="new-password"
          placeholder="Password"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          required
        />
        <label
          className="small"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          title="Each question spends money on inference. Off unless ticked."
        >
          <input
            type="checkbox"
            checked={newCanAsk}
            onChange={e => setNewCanAsk(e.target.checked)}
          />
          Allow Ask
        </label>
        <button type="submit">Add user</button>
      </form>
    </div>
  );
};

export default UsersPanel;
